import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Play, Pause, Square, BellOff } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { createAudioContext, startAlarmLoop } from "../lib/alarmAudio";

/**
 * TIME Watch → Utility → Timer
 * Classico timer stile app Orologio dello smartphone.
 * - Impostazione ore / minuti / secondi
 * - Avvio, pausa, ripresa, azzeramento
 * - Countdown in tempo reale
 * - Al termine: suono (Web Audio) + notifica visiva
 * Nessuna funzione extra (no cronologia, preset, statistiche).
 */

const clampInt = (v, min, max) => {
  const n = parseInt(v, 10);
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
};

const pad2 = (n) => String(n).padStart(2, "0");

export default function TimeWatchTimerPage() {
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(5);
  const [seconds, setSeconds] = useState(0);

  // remainingMs: quanto manca alla scadenza. Solo valido quando running/paused.
  const [remainingMs, setRemainingMs] = useState(0);
  const [status, setStatus] = useState("idle"); // idle | running | paused | finished

  const endAtRef = useRef(null); // timestamp scadenza quando running
  const tickIvRef = useRef(null);
  const audioCtxRef = useRef(null);
  const stopLoopRef = useRef(null); // funzione stop del loop beep

  const totalConfiguredMs =
    (hours * 3600 + minutes * 60 + seconds) * 1000;

  // Loop del countdown via setInterval (250ms).
  // NB: usiamo setInterval e NON requestAnimationFrame, così il timer
  // continua a "vedere" la scadenza anche quando il tab è in background
  // (i browser throttlano ma setInterval continua a girare, mentre RAF
  // viene sospeso). Il valore mostrato ridà in vita al ritorno in foreground.
  useEffect(() => {
    if (status !== "running") return;

    const tick = () => {
      const now = Date.now();
      const left = Math.max(0, endAtRef.current - now);
      setRemainingMs(left);
      if (left <= 0) {
        setStatus("finished");
      }
    };
    tick();
    tickIvRef.current = setInterval(tick, 250);

    return () => {
      if (tickIvRef.current) {
        clearInterval(tickIvRef.current);
        tickIvRef.current = null;
      }
    };
  }, [status]);

  // Rientro in foreground: ricontrolla immediatamente la scadenza,
  // così se il tab era in background e il timer è scaduto, il popup
  // "Interrompi" si apre appena l'utente torna.
  useEffect(() => {
    if (status !== "running") return;
    const onVis = () => {
      if (document.visibilityState === "visible" && endAtRef.current) {
        const left = Math.max(0, endAtRef.current - Date.now());
        setRemainingMs(left);
        if (left <= 0) setStatus("finished");
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [status]);

  // Quando entra in "finished": avvia il loop beep (pattern Timer 1-1-1).
  // Quando esce da "finished": ferma il loop.
  useEffect(() => {
    if (status === "finished") {
      if (!audioCtxRef.current) audioCtxRef.current = createAudioContext();
      // Notifica di sistema opzionale se il tab non è visibile.
      maybeShowSystemNotification();
      // Timer → 1 beep per ciclo (aggiornamento Sprint).
      stopLoopRef.current = startAlarmLoop(audioCtxRef.current, [1]);
    }
    return () => {
      if (stopLoopRef.current) {
        stopLoopRef.current();
        stopLoopRef.current = null;
      }
    };
  }, [status]);

  const ensureAudio = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = createAudioContext();
    }
    // Sblocca il context su gesto utente (autoplay policy iOS/Safari).
    if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume().catch(() => {});
    }
    return audioCtxRef.current;
  };

  const maybeShowSystemNotification = () => {
    if (
      typeof window === "undefined" ||
      !("Notification" in window) ||
      Notification.permission !== "granted" ||
      document.visibilityState === "visible"
    )
      return;
    try {
      new Notification("Timer", {
        body: "Tempo scaduto",
        tag: "time-watch-timer",
        renotify: true,
      });
    } catch {
      /* ignore */
    }
  };

  const handleStart = () => {
    if (status === "running") return;
    const startFromMs =
      status === "paused" ? remainingMs : totalConfiguredMs;
    if (startFromMs <= 0) return;
    ensureAudio(); // sblocca l'audio grazie al gesto utente
    // Richiedi il permesso notifiche in modo trasparente (una sola volta).
    if (
      typeof window !== "undefined" &&
      "Notification" in window &&
      Notification.permission === "default"
    ) {
      Notification.requestPermission().catch(() => {});
    }
    endAtRef.current = Date.now() + startFromMs;
    setRemainingMs(startFromMs);
    setStatus("running");
  };

  const handlePause = () => {
    if (status !== "running") return;
    if (tickIvRef.current) {
      clearInterval(tickIvRef.current);
      tickIvRef.current = null;
    }
    const left = Math.max(0, endAtRef.current - Date.now());
    setRemainingMs(left);
    setStatus("paused");
  };

  const handleReset = () => {
    if (tickIvRef.current) {
      clearInterval(tickIvRef.current);
      tickIvRef.current = null;
    }
    endAtRef.current = null;
    setRemainingMs(0);
    setStatus("idle");
  };

  // Interrompi: chiude il popup e ferma il suono; il timer torna in idle.
  const handleInterrompi = () => {
    if (stopLoopRef.current) {
      stopLoopRef.current();
      stopLoopRef.current = null;
    }
    endAtRef.current = null;
    setRemainingMs(0);
    setStatus("idle");
  };

  // Display: quando idle mostra il tempo configurato, altrimenti quello che scorre.
  const displayMs =
    status === "idle" ? totalConfiguredMs : remainingMs;
  const displayH = Math.floor(displayMs / 3600000);
  const displayM = Math.floor((displayMs % 3600000) / 60000);
  const displayS = Math.floor((displayMs % 60000) / 1000);

  const isEditable = status === "idle";
  const canStart =
    (status === "idle" && totalConfiguredMs > 0) || status === "paused";

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20 max-w-3xl mx-auto">
      <Link
        to="/innovazione/time-watch/utility"
        data-testid="time-watch-timer-back-link"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-white/40 hover:text-white/80 transition mb-10"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        Utility
      </Link>

      <PageHeader
        eyebrow="Utility"
        title="Timer"
        description="Imposta ore, minuti e secondi e avvia il conto alla rovescia."
        testId="time-watch-timer-header"
      />

      {/* Display */}
      <div
        data-testid="timer-display"
        className={`glass-card p-8 sm:p-12 mb-8 text-center transition-colors ${
          status === "finished"
            ? "border-emerald-400/60 shadow-[0_0_60px_-10px_rgba(16,185,129,0.5)] animate-pulse"
            : ""
        }`}
      >
        {isEditable ? (
          <div
            data-testid="timer-inputs"
            className="flex items-end justify-center gap-3 sm:gap-6"
          >
            <TimeInput
              value={hours}
              onChange={(v) => setHours(clampInt(v, 0, 99))}
              label="ore"
              testId="timer-input-hours"
            />
            <Separator />
            <TimeInput
              value={minutes}
              onChange={(v) => setMinutes(clampInt(v, 0, 59))}
              label="min"
              testId="timer-input-minutes"
            />
            <Separator />
            <TimeInput
              value={seconds}
              onChange={(v) => setSeconds(clampInt(v, 0, 59))}
              label="sec"
              testId="timer-input-seconds"
            />
          </div>
        ) : (
          <div
            data-testid="timer-countdown"
            className="font-extralight text-white tracking-tight tabular-nums text-6xl sm:text-7xl lg:text-8xl leading-none"
          >
            {pad2(displayH)}
            <span className="text-white/30 mx-1">:</span>
            {pad2(displayM)}
            <span className="text-white/30 mx-1">:</span>
            {pad2(displayS)}
          </div>
        )}

        {status === "finished" && (
          <div
            data-testid="timer-finished-notice"
            className="mt-6 text-[11px] tracking-[0.32em] uppercase text-emerald-300"
          >
            Tempo scaduto
          </div>
        )}
        {status === "paused" && (
          <div
            data-testid="timer-paused-notice"
            className="mt-6 text-[11px] tracking-[0.32em] uppercase text-white/40"
          >
            In pausa
          </div>
        )}
      </div>

      {/* Controls */}
      <div
        data-testid="timer-controls"
        className="flex items-center justify-center gap-4"
      >
        {status !== "running" ? (
          <button
            type="button"
            onClick={handleStart}
            disabled={!canStart}
            data-testid="timer-start-button"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500/90 text-black text-sm tracking-wide font-medium hover:bg-emerald-400 transition disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Play className="w-4 h-4" />
            {status === "paused" ? "Riprendi" : "Avvia"}
          </button>
        ) : (
          <button
            type="button"
            onClick={handlePause}
            data-testid="timer-pause-button"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 border border-white/15 text-white text-sm tracking-wide hover:bg-white/15 transition"
          >
            <Pause className="w-4 h-4" />
            Pausa
          </button>
        )}

        <button
          type="button"
          onClick={handleReset}
          disabled={status === "idle"}
          data-testid="timer-reset-button"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-transparent border border-white/15 text-white/70 text-sm tracking-wide hover:text-white hover:border-white/30 transition disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Square className="w-4 h-4" />
          Azzera
        </button>
      </div>

      {/* Popup "Tempo scaduto" — Interrompi ferma il suono e chiude l'allarme */}
      {status === "finished" && (
        <TimerFinishedOverlay
          totalConfiguredMs={totalConfiguredMs}
          onInterrompi={handleInterrompi}
        />
      )}
    </div>
  );
}

function TimerFinishedOverlay({ totalConfiguredMs, onInterrompi }) {
  const h = Math.floor(totalConfiguredMs / 3600000);
  const m = Math.floor((totalConfiguredMs % 3600000) / 60000);
  const s = Math.floor((totalConfiguredMs % 60000) / 1000);
  return (
    <div
      data-testid="timer-finished-modal"
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center p-6"
    >
      <div className="text-[11px] tracking-[0.32em] uppercase text-emerald-300 mb-8 animate-pulse">
        Tempo scaduto
      </div>
      <div
        data-testid="timer-finished-value"
        className="text-white font-extralight tabular-nums leading-none text-6xl sm:text-8xl lg:text-9xl"
      >
        {pad2(h)}
        <span className="text-white/25 mx-1">:</span>
        {pad2(m)}
        <span className="text-white/25 mx-1">:</span>
        {pad2(s)}
      </div>
      <div className="mt-6 text-sm sm:text-base font-light text-white/50 max-w-md text-center">
        Il timer è terminato.
      </div>
      <button
        type="button"
        onClick={onInterrompi}
        data-testid="timer-interrompi-button"
        autoFocus
        className="mt-14 inline-flex items-center gap-2 px-10 py-4 rounded-full bg-red-500/90 text-white text-sm tracking-wide font-medium hover:bg-red-400 transition"
      >
        <BellOff className="w-4 h-4" />
        Interrompi
      </button>
    </div>
  );
}

function TimeInput({ value, onChange, label, testId }) {
  return (
    <label className="flex flex-col items-center">
      <input
        type="number"
        min={0}
        max={99}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-20 sm:w-28 bg-transparent border-b border-white/15 focus:border-emerald-400/60 outline-none text-center font-extralight text-white tabular-nums text-5xl sm:text-6xl lg:text-7xl leading-none pb-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <span className="mt-3 text-[10px] tracking-[0.32em] uppercase text-white/40">
        {label}
      </span>
    </label>
  );
}

function Separator() {
  return (
    <span className="pb-8 sm:pb-10 text-4xl sm:text-5xl text-white/25 font-extralight">
      :
    </span>
  );
}
